import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  inject,
  OnInit,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

// Kendo UI Modules
import { InputsModule, CheckBoxModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';

// Icons
import {
  xIcon,
  saveIcon,
  infoCircleIcon,
  exclamationCircleIcon,
  plusIcon,
  minusIcon,
  gearIcon,
  clockIcon,
  bugOutlineIcon,
  envelopIcon,
  laptopOutlineIcon,
  securityCheckOutlineIcon,
  userIcon,
  arrowLeftIcon,
} from '@progress/kendo-svg-icons';

// Models
import {
  ServiceType,
  ModificationAction,
  ServiceInfo,
  SERVICE_CONFIGURATIONS,
  FormFieldConfig,
} from '../../models/customer-details.model';

// Base Component
import { BaseComponent } from 'src/app/core/shared/common/base.component';

// Service Components
import { MedrServiceComponent } from '../../../customer-onboarding/components/medr-service/medr-service.component';
import { MesServiceComponent } from '../../../customer-onboarding/components/mes-service/mes-service.component';
import { MvssServiceComponent } from '../../../customer-onboarding/components/mvss-service/mvss-service.component';
import { MxdrServiceComponent } from '../../../customer-onboarding/components/mxdr-service/mxdr-service.component';
import { ProfessionalServicesComponent } from '../../../customer-onboarding/components/professional-services/professional-services.component';

export interface ServiceModificationSlideoutData {
  serviceType?: ServiceType;
  action: ModificationAction;
  existingService?: ServiceInfo;
  existingServices?: ServiceType[]; // Array of service types customer already has
}

export interface ServiceModificationSlideoutResult {
  serviceType: ServiceType;
  action: ModificationAction;
  formData: any;
}

@Component({
  selector: 'app-service-modification-slideout',
  standalone: true,
  templateUrl: './service-modification-slideout.component.html',
  styleUrls: ['./service-modification-slideout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputsModule,
    CheckBoxModule,
    DropDownsModule,
    DateInputsModule,
    LabelModule,
    ButtonsModule,
    IconsModule,
    SwitchModule,
    LayoutModule,
    // Service Components
    MedrServiceComponent,
    MesServiceComponent,
    MvssServiceComponent,
    MxdrServiceComponent,
    ProfessionalServicesComponent,
  ],
})
export class ServiceModificationSlideoutComponent extends BaseComponent implements OnInit {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  // Input/Output
  public slideoutData = input<ServiceModificationSlideoutData | null>();
  public isOpen = input<boolean>(false);
  public result = output<ServiceModificationSlideoutResult>();
  public cancelled = output<void>();

  // Kendo UI SVG Icons
  public xIcon = xIcon;
  public saveIcon = saveIcon;
  public infoCircleIcon = infoCircleIcon;
  public exclamationCircleIcon = exclamationCircleIcon;
  public plusIcon = plusIcon;
  public minusIcon = minusIcon;
  public gearIcon = gearIcon;
  public clockIcon = clockIcon;
  public bugOutlineIcon = bugOutlineIcon;
  public envelopIcon = envelopIcon;
  public laptopOutlineIcon = laptopOutlineIcon;
  public securityCheckOutlineIcon = securityCheckOutlineIcon;
  public userIcon = userIcon;
  public arrowLeftIcon = arrowLeftIcon;

  // Enum references for template
  public ServiceType = ServiceType;

  // Available services data
  public availableServices = [
    { value: ServiceType.MXDR, label: 'mXDR (Extended Detection & Response)', icon: this.securityCheckOutlineIcon },
    { value: ServiceType.MEDR, label: 'mEDR (Endpoint Detection & Response)', icon: this.laptopOutlineIcon },
    { value: ServiceType.VSS, label: 'VSS (Vulnerability Scanning Service)', icon: this.bugOutlineIcon },
    { value: ServiceType.MES, label: 'mES (Email Security)', icon: this.envelopIcon },
    { value: ServiceType.PROFESSIONAL_SERVICES, label: 'Professional Services', icon: this.userIcon },
  ];

  // Signal to track selected service for addition
  public selectedService = signal<ServiceType | null>(null);

  // Internal signals to manage slideout data
  public internalSlideoutData = signal<ServiceModificationSlideoutData | null>(null);
  private internalIsOpen = signal<boolean>(false);

  // Service form data signals
  public serviceFormData = signal<any>(null);
  public serviceFormValid = signal<boolean>(false);

  // Form
  public serviceForm: FormGroup | null = null;

  // Loading state
  public isFormReady = computed(() => {
    return this.serviceForm !== null && this.serviceConfig() !== null;
  });

  // Computed values
  public serviceConfig = computed(() => {
    const data = this.internalSlideoutData();
    const selectedService = this.selectedService();
    const serviceType = selectedService || data?.serviceType;
    return serviceType ? SERVICE_CONFIGURATIONS[serviceType] : null;
  });

  // Show service selection cards when action is ADD and no specific service is selected
  public showServiceSelection = computed(() => {
    const data = this.internalSlideoutData();
    return data?.action === ModificationAction.ADD && !data?.serviceType && !this.selectedService();
  });

  // Show service configuration when a service is selected or modifying existing service
  public showServiceConfiguration = computed(() => {
    const data = this.internalSlideoutData();
    const selectedService = this.selectedService();
    return (
      (data?.action === ModificationAction.ADD && selectedService) ||
      (data?.action === ModificationAction.MODIFY && data?.serviceType) ||
      (data?.serviceType && !this.showServiceSelection())
    );
  });

  // Filter available services to exclude those customer already has
  public filteredServices = computed(() => {
    const data = this.internalSlideoutData();
    const existingServices = data?.existingServices || [];
    return this.availableServices.filter((service) => !existingServices.includes(service.value));
  });

  public slideoutTitle = computed(() => {
    const data = this.internalSlideoutData();
    const selectedService = this.selectedService();

    if (this.showServiceSelection()) {
      return 'Add Service';
    }

    if (this.showServiceConfiguration()) {
      const serviceType = selectedService || data?.serviceType;
      const config = serviceType ? SERVICE_CONFIGURATIONS[serviceType] : null;
      if (config) {
        const actionText =
          data?.action === ModificationAction.ADD
            ? 'Add'
            : data?.action === ModificationAction.MODIFY
              ? 'Modify'
              : 'Cancel';
        return `${actionText} ${config.title}`;
      }
    }

    return 'Service Modification';
  });

  public isFormValid = computed(() => {
    if (this.showServiceSelection()) {
      return false; // No form validation needed for service selection
    }

    if (this.showServiceConfiguration()) {
      return this.serviceFormValid();
    }

    return this.serviceForm?.valid;
  });

  public visibleFields = computed(() => {
    const config = this.serviceConfig();
    if (!config || !this.serviceForm) return [];

    return config.fields.filter((field) => this.isFieldVisible(field));
  });

  constructor() {
    super();

    // Watch for slideoutData changes and initialize form when available
    effect(() => {
      const data = this.internalSlideoutData();
      if (data && this.internalIsOpen()) {
        this.initializeForm();
      }
    });
  }

  // Method to set slideout data from parent component
  public setSlideoutData(data: ServiceModificationSlideoutData): void {
    this.internalSlideoutData.set(data);
    this.internalIsOpen.set(true);

    // If we have existing service data and a service type, initialize the form data
    if (data.existingService && data.serviceType) {
      const initialData = this.convertServiceInfoToFormData(data.existingService);
      this.serviceFormData.set(initialData);
    }

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    // Don't initialize form here - wait for slideoutData to be available
  }

  private initializeForm(): void {
    const config = this.serviceConfig();
    if (!config) return;

    const formGroup: any = {};

    // Create form controls for each field
    config.fields.forEach((field) => {
      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.type === 'number') {
        if (field.validation?.min !== undefined) {
          validators.push(Validators.min(field.validation.min));
        }
        if (field.validation?.max !== undefined) {
          validators.push(Validators.max(field.validation.max));
        }
      }

      if (field.validation?.pattern) {
        validators.push(Validators.pattern(field.validation.pattern));
      }

      formGroup[field.fieldName] = [this.getDefaultValue(field), validators];
    });

    this.serviceForm = this.fb.group(formGroup);

    // Populate form with existing service data if modifying
    const data = this.internalSlideoutData();
    if (data?.existingService) {
      this.populateFormWithExistingService(data.existingService);
    }

    this.cdr.detectChanges();
  }

  private getDefaultValue(field: FormFieldConfig): any {
    switch (field.type) {
      case 'boolean':
        return false;
      case 'number':
        return 0;
      case 'select':
        return null;
      case 'multiselect':
        return [];
      default:
        return '';
    }
  }

  private populateFormWithExistingService(service: ServiceInfo): void {
    if (!this.serviceForm) return;

    const formData: any = {};

    switch (service.serviceType) {
      case ServiceType.MXDR:
        const mxdrService = service as any;
        formData.mxdrType = mxdrService.mxdrType;
        formData.extendedLogRetention = mxdrService.extendedLogRetention;
        formData.govCloud = mxdrService.govCloud;
        formData.quantityAdUsers = mxdrService.quantityAdUsers;
        formData.quantityServers = mxdrService.quantityServers;
        formData.quantityAppliances = mxdrService.quantityAppliances;
        formData.virtualAppliance = mxdrService.virtualAppliance;
        formData.applianceModels = mxdrService.applianceModels;
        break;

      case ServiceType.MEDR:
        const medrService = service as any;
        formData.medrNetworkDiscovery = medrService.medrNetworkDiscovery;
        formData.totalMachines = medrService.totalMachines;
        break;

      case ServiceType.PROFESSIONAL_SERVICES:
        const psService = service as any;
        formData.penetrationTestStartDate = psService.penetrationTestStartDate;
        formData.attackSurfaceSubnets = psService.attackSurfaceSubnets;
        formData.attackSurfaceStartDate = psService.attackSurfaceStartDate;
        formData.breachAttackPackage = psService.breachAttackPackage;
        formData.breachAttackStartDate = psService.breachAttackStartDate;
        break;
    }

    this.serviceForm?.patchValue(formData);
  }

  private isFieldVisible(field: FormFieldConfig): boolean {
    if (!field.dependencies || !this.serviceForm) return true;

    return field.dependencies.every((dependency) => {
      const fieldValue = this.serviceForm?.get(dependency.field)?.value;

      switch (dependency.operator) {
        case 'equals':
          return fieldValue === dependency.value;
        case 'not_equals':
          return fieldValue !== dependency.value;
        case 'contains':
          return Array.isArray(fieldValue) ? fieldValue.includes(dependency.value) : false;
        case 'greater_than':
          return typeof fieldValue === 'number' && fieldValue > dependency.value;
        case 'less_than':
          return typeof fieldValue === 'number' && fieldValue < dependency.value;
        default:
          return true;
      }
    });
  }

  public onSave(): void {
    if (this.isFormValid()) {
      const data = this.internalSlideoutData();
      const selectedService = this.selectedService();
      if (!data) return;

      // Use selected service if available, otherwise use the service type from data
      const serviceType = selectedService || data.serviceType;
      if (!serviceType) return;

      const result: ServiceModificationSlideoutResult = {
        serviceType: serviceType,
        action: data.action,
        formData: this.serviceFormData() || this.serviceForm?.value || {},
      };

      this.result.emit(result);
    }
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  // Service card interaction methods
  public selectService(serviceType: ServiceType): void {
    this.selectedService.set(serviceType);

    // Check if we have existing service data for this service type
    const data = this.internalSlideoutData();
    let initialData = null;

    if (data?.existingService && data.existingService.serviceType === serviceType) {
      // Convert existing service data to the format expected by service components
      initialData = this.convertServiceInfoToFormData(data.existingService);
    }

    // Set initial data for the service component
    this.serviceFormData.set(initialData);

    // Don't reset validity immediately - let the service component emit its validity
    // this.serviceFormValid.set(false);

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  public isServiceSelected(serviceType: ServiceType): boolean {
    return this.selectedService() === serviceType;
  }

  // Navigation methods
  public goBackToServiceSelection(): void {
    this.selectedService.set(null);
    this.serviceFormData.set(null);
    this.serviceFormValid.set(false);

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  // Service form data handlers
  public onServiceFormDataUpdate(data: any): void {
    this.serviceFormData.set(data);
  }

  public onServiceFormValidityUpdate(isValid: boolean): void {
    this.serviceFormValid.set(isValid);
  }

  private convertServiceInfoToFormData(serviceInfo: ServiceInfo): any {
    // Convert ServiceInfo to the format expected by service components
    switch (serviceInfo.serviceType) {
      case ServiceType.MXDR:
        return {
          selectedService: (serviceInfo as any).mxdrType || 'xdr-standard',
          extendedLogRetention: (serviceInfo as any).extendedLogRetention || false,
          govCloud: (serviceInfo as any).govCloud || false,
          quantityAdUsers: (serviceInfo as any).quantityAdUsers || 0,
          quantityServers: (serviceInfo as any).quantityServers || 0,
          quantityAppliances: (serviceInfo as any).quantityAppliances || 0,
          virtualAppliance: (serviceInfo as any).virtualAppliance || false,
          applianceModels: (serviceInfo as any).applianceModels || [],
        };

      case ServiceType.MEDR:
        return {
          selectedService: 'edr-complete', // Default to complete for existing services
          medrNetworkDiscovery: (serviceInfo as any).medrNetworkDiscovery || false,
          totalMachines: (serviceInfo as any).totalMachines || 0,
        };

      case ServiceType.VSS:
        return {
          selectedService: 'vss-enhanced', // Default to enhanced for existing services
          totalMachines: (serviceInfo as any).totalMachines || 0,
        };

      case ServiceType.MES:
        return {
          selectedService: 'mes-complete', // Default to complete for existing services
          totalMachines: (serviceInfo as any).totalMachines || 0,
        };

      case ServiceType.PROFESSIONAL_SERVICES:
        return {
          penetrationTesting: true, // Assume existing services have at least one service
          incidentResponse: false,
          vciso: false,
          attackSurfaceAssessment: false,
          breachAttackAssessment: false,
          penetrationTestStartDate: (serviceInfo as any).penetrationTestStartDate || null,
          attackSurfaceSubnets: (serviceInfo as any).attackSurfaceSubnets || '',
          attackSurfaceStartDate: (serviceInfo as any).attackSurfaceStartDate || null,
          breachAttackPackage: (serviceInfo as any).breachAttackPackage || '',
          breachAttackStartDate: (serviceInfo as any).breachAttackStartDate || null,
        };

      default:
        return null;
    }
  }

  public getServiceDescription(serviceType: ServiceType): string {
    const descriptions: { [key in ServiceType]: string } = {
      [ServiceType.MXDR]: 'Extended Detection & Response with advanced threat hunting',
      [ServiceType.MEDR]: 'Endpoint Detection & Response for device protection',
      [ServiceType.VSS]: 'Vulnerability scanning and assessment services',
      [ServiceType.MES]: 'Email security and threat protection',
      [ServiceType.PROFESSIONAL_SERVICES]: 'Professional services including penetration testing and consulting',
    };
    return descriptions[serviceType] || 'Service description not available';
  }

  public getServiceIconBackground(serviceType: ServiceType): string {
    const backgrounds: { [key in ServiceType]: string } = {
      [ServiceType.MXDR]: '#e3f2fd', // Light blue
      [ServiceType.MEDR]: '#f3e5f5', // Light purple
      [ServiceType.VSS]: '#fff3e0', // Light orange
      [ServiceType.MES]: '#e8f5e8', // Light green
      [ServiceType.PROFESSIONAL_SERVICES]: '#fce4ec', // Light pink
    };
    return backgrounds[serviceType] || '#f5f5f5'; // Default light gray
  }

  public getFieldError(fieldName: string): string | null {
    if (!this.serviceForm) return null;
    const control = this.serviceForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return null;

    if (control.errors['required']) {
      return 'This field is required';
    }
    if (control.errors['min']) {
      return `Minimum value is ${control.errors['min'].min}`;
    }
    if (control.errors['max']) {
      return `Maximum value is ${control.errors['max'].max}`;
    }
    if (control.errors['pattern']) {
      return 'Invalid format';
    }

    return 'Invalid value';
  }
}
